import { API_BASE_URL, getActiveArtistId, getAuthToken } from "@/api/client";
import { recordStubbedPath } from "@/lib/stubRegistry";

/** Lightweight fetch helpers for the Agents API */
const DEFAULT_BASE = "/api";
const BASE_URL = API_BASE_URL || DEFAULT_BASE;

export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";

type ParamPrimitive = string | number | boolean;
type ParamValue = ParamPrimitive | null | undefined | ParamPrimitive[];

export interface ApiRequestOptions extends RequestInit {
  params?: Record<string, ParamValue>;
}

function headersToRecord(headers?: HeadersInit): Record<string, string> {
  const normalized: Record<string, string> = {};
  if (!headers) {
    return normalized;
  }
  if (typeof Headers !== "undefined" && headers instanceof Headers) {
    headers.forEach((value, key) => {
      normalized[key] = value;
    });
    return normalized;
  }
  if (Array.isArray(headers)) {
    for (const [key, value] of headers) {
      if (key && value) {
        normalized[key] = value;
      }
    }
    return normalized;
  }
  if (typeof headers === "object") {
    Object.assign(normalized, headers);
  }
  return normalized;
}

function headerExists(headers: Record<string, string>, name: string): boolean {
  const lowerName = name.toLowerCase();
  return Object.keys(headers).some((key) => key.toLowerCase() === lowerName);
}

function setHeaderIfMissing(headers: Record<string, string>, name: string, value: string): void {
  if (!headerExists(headers, name)) {
    headers[name] = value;
  }
}

export function getApiBaseUrl(): string {
  return BASE_URL || DEFAULT_BASE;
}

export function resolveApiUrl(path: string): string {
  const base = getApiBaseUrl();
  const normalizedBase = base ? base.replace(/\/+$/, "") : "";
  const suffix = path.startsWith("/") ? path : `/${path}`;
  if (!normalizedBase) {
    return suffix;
  }
  if (suffix === "/") {
    return normalizedBase;
  }
  return `${normalizedBase}${suffix}`;
}

function appendQueryParams(url: string, params?: Record<string, ParamValue>): string {
  if (!params) {
    return url;
  }
  const query = new URLSearchParams();
  const appendValue = (key: string, value: ParamPrimitive) => {
    query.append(key, String(value));
  };
  for (const [key, value] of Object.entries(params)) {
    if (value == null) {
      continue;
    }
    if (Array.isArray(value)) {
      value.forEach((entry) => {
        if (entry == null) {
          return;
        }
        appendValue(key, entry);
      });
    } else {
      appendValue(key, value);
    }
  }

  const qs = query.toString();
  if (!qs) {
    return url;
  }
  return url.includes("?") ? `${url}&${qs}` : `${url}?${qs}`;
}

async function baseApi<T>(path: string, init?: ApiRequestOptions): Promise<T> {
  const { params, headers, ...rest } = init ?? {};
  const url = appendQueryParams(resolveApiUrl(path), params);

  const finalHeaders = headersToRecord(headers);
  setHeaderIfMissing(finalHeaders, "Accept", "application/json");

  const token = getAuthToken();
  if (token) {
    setHeaderIfMissing(finalHeaders, "Authorization", `Bearer ${token}`);
  }

  const artistId = getActiveArtistId();
  if (artistId) {
    setHeaderIfMissing(finalHeaders, "X-Artist-Id", artistId);
  }

  const res = await fetch(url, {
    ...rest,
    headers: finalHeaders,
  });

  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(`API ${res.status} ${res.statusText}: ${text || url}`);
  }

  const ctype = res.headers.get("content-type") || "";
  if (!ctype.includes("application/json")) {
    return undefined as unknown as T;
  }

  const payload = await res.json();
  return unwrapStubbedResponse<T>(payload, path);
}

export async function get<T>(path: string, options?: ApiRequestOptions): Promise<T> {
  return baseApi<T>(path, { ...(options ?? {}), method: "GET" });
}

export async function post<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
  const { headers, ...rest } = options ?? {};
  const finalHeaders: HeadersInit = {
    "Content-Type": "application/json",
    ...(headers ?? {}),
  };
  const init: ApiRequestOptions = {
    ...rest,
    headers: finalHeaders,
    method: "POST",
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  return baseApi<T>(path, init);
}

export async function patch<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T> {
  const { headers, ...rest } = options ?? {};
  const finalHeaders: HeadersInit = {
    "Content-Type": "application/json",
    ...(headers ?? {}),
  };
  const init: ApiRequestOptions = {
    ...rest,
    headers: finalHeaders,
    method: "PATCH",
  };
  if (body !== undefined) {
    init.body = JSON.stringify(body);
  }
  return baseApi<T>(path, init);
}

export async function del<T>(path: string, options?: ApiRequestOptions): Promise<T> {
  return baseApi<T>(path, {
    ...(options ?? {}),
    method: "DELETE",
  });
}

export async function upload<T>(path: string, body: FormData, options?: ApiRequestOptions): Promise<T> {
  const init: ApiRequestOptions = {
    ...(options ?? {}),
    method: options?.method ?? "POST",
    body,
  };
  return baseApi<T>(path, init);
}

export async function ping(): Promise<{ status: "ok" } | undefined> {
  try {
    return await get<{ status: "ok" }>("/health");
  } catch {
    return undefined;
  }
}

interface ApiClient {
  <T>(path: string, init?: ApiRequestOptions): Promise<T>;
  get<T>(path: string, options?: ApiRequestOptions): Promise<T>;
  post<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T>;
  patch<T>(path: string, body?: unknown, options?: ApiRequestOptions): Promise<T>;
  delete<T>(path: string, options?: ApiRequestOptions): Promise<T>;
  upload<T>(path: string, body: FormData, options?: ApiRequestOptions): Promise<T>;
}

const api = Object.assign(baseApi, {
  get,
  post,
  patch,
  delete: del,
  upload,
}) as ApiClient;

export { api };

function markStubbedValue<T>(value: T): T {
  if (value && typeof value === "object") {
    Object.defineProperty(value, "__stubbed", {
      value: true,
      enumerable: false,
      configurable: false,
      writable: false,
    });
  }
  return value;
}

export function unwrapStubbedResponse<T>(raw: unknown, path: string): T {
  if (raw && typeof raw === "object" && "stubbed" in raw) {
    recordStubbedPath(path);
    const envelope = raw as { data?: unknown; use_fallback?: boolean };
    if (envelope.use_fallback) {
      return undefined as T;
    }
    return markStubbedValue(envelope.data as T);
  }
  return raw as T;
}
